import type {
  EmployeeStampProgress,
  LearningResource,
  PassportPageSummary,
  Stamp,
} from "@peer-start/platform-contracts";
import { Check, Circle } from "lucide-react";
import { useTranslation } from "react-i18next";

export type CompetencySkillItem = {
  stamp: Stamp;
  progress?: EmployeeStampProgress;
  taskCount: number;
  current: boolean;
  required: boolean;
};

function skillTone(item: CompetencySkillItem): "complete" | "current" | "upcoming" {
  // Selection (current) wins for emphasis; completion is shown via the green check marker.
  if (item.current) return "current";
  if (item.progress?.status === "established") return "complete";
  return "upcoming";
}

function isSkillComplete(item: CompetencySkillItem): boolean {
  return item.progress?.status === "established";
}

export function buildCompetencySkillItems({
  summary,
  stamps,
  progress,
  resources,
  currentStampId,
}: {
  summary: PassportPageSummary;
  stamps: Stamp[];
  progress: EmployeeStampProgress[];
  resources: LearningResource[];
  currentStampId?: string;
}): CompetencySkillItem[] {
  const progressByStamp = new Map(progress.map((entry) => [entry.stampId, entry]));
  const pageStampOrder = new Map(summary.page.stampIds.map((id, index) => [id, index]));

  return stamps
    .filter((stamp) => stamp.pageIds.includes(summary.page.id))
    .filter((stamp) => progressByStamp.get(stamp.id)?.applicability !== "out_of_scope")
    .map((stamp) => ({
      stamp,
      progress: progressByStamp.get(stamp.id),
      taskCount: resources.filter((resource) => resource.stampIds.includes(stamp.id)).length ||
        stamp.skillCriterionIds.length + stamp.knowledgeCriterionIds.length + stamp.behaviourCriterionIds.length,
      current: stamp.id === currentStampId,
      required: progressByStamp.get(stamp.id)?.applicability === "required",
    }))
    .sort(
      (left, right) =>
        (pageStampOrder.get(left.stamp.id) ?? Number.MAX_SAFE_INTEGER) -
        (pageStampOrder.get(right.stamp.id) ?? Number.MAX_SAFE_INTEGER),
    );
}

export function LearningRouteCompetencySkillsList({
  items,
  onSelectStamp,
  controlIdPrefix = "learning-route.skill",
  showFocusHint = false,
}: {
  items: CompetencySkillItem[];
  onSelectStamp?: (stampId: string) => void;
  controlIdPrefix?: string;
  showFocusHint?: boolean;
}) {
  const { t } = useTranslation();

  if (!items.length) {
    return <p className="text-sm text-muted-foreground">{t("learningRoute.noSkills")}</p>;
  }

  return (
    <ol className="relative space-y-0">
      {items.map((item, index) => {
        const tone = skillTone(item);
        const complete = isSkillComplete(item);
        const titleClass =
          tone === "current"
            ? "font-medium text-info-foreground"
            : tone === "complete"
              ? "font-normal text-success-foreground"
              : "font-normal text-foreground";

        const marker = (
          <span
            aria-hidden="true"
            className={`relative z-10 mt-0.5 grid size-5 shrink-0 place-items-center rounded-full ${
              complete
                ? "bg-success-foreground text-primary-foreground"
                : tone === "current"
                  ? "bg-info-foreground text-primary-foreground"
                  : "border border-border bg-background text-muted-foreground"
            }`}
          >
            {complete ? (
              <Check className="size-2.5" strokeWidth={2.6} />
            ) : tone === "current" ? (
              <span className="size-1.5 rounded-full bg-primary-foreground" />
            ) : (
              <Circle className="size-2.5 opacity-0" />
            )}
          </span>
        );

        const copy = (
          <span className="min-w-0 flex-1">
            <span className={`block text-sm leading-snug ${titleClass}`}>{item.stamp.title}</span>
            <span
              className={`mt-1 block text-xs ${
                tone === "current" ? "font-medium text-info-foreground" : "text-muted-foreground"
              }`}
            >
              {t("learningRoute.tasks", { count: item.taskCount })}
            </span>
          </span>
        );

        return (
          <li className="relative flex gap-3 pb-5 last:pb-0" key={item.stamp.id}>
            {index < items.length - 1 ? (
              <span aria-hidden="true" className="absolute top-5 bottom-0 left-[0.55rem] w-px bg-border" />
            ) : null}
            {onSelectStamp ? (
              <button
                aria-current={item.current ? "true" : undefined}
                className="group flex min-w-0 flex-1 items-start gap-3 rounded-lg text-left focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
                data-control-id={`${controlIdPrefix}.${item.stamp.id}`}
                onClick={() => onSelectStamp(item.stamp.id)}
                type="button"
              >
                {marker}
                {copy}
                {showFocusHint ? (
                  <span
                    className={`mt-0.5 shrink-0 text-xs font-medium opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 ${
                      item.current ? "text-info-foreground" : "text-muted-foreground"
                    }`}
                  >
                    {item.current ? t("learningRoute.focusCurrent") : t("learningRoute.focusOn")}
                  </span>
                ) : null}
              </button>
            ) : (
              <div className="flex min-w-0 flex-1 items-start gap-3">
                {marker}
                {copy}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
